import React, { useState } from "react"
import { Dialog, DialogTitle, DialogContent, Box, Divider, DialogActions, Tabs, Tab, Button } from "@material-ui/core"
import { NavLink, useParams } from "react-router-dom"
import { DialogTitleCloser } from "../Creators/DialogTitleCloser"
import { InlineTitleCloser } from "../Creators"
import { InlineTitle, RowBox } from ".."
import { UserPanel } from "../UserPanel"
import { useLazyQueryAdminableUser, useMutationResendInvite } from "../../data"
import { toId } from "../../utils"
import { PersonTraining } from "./PersonTraining"
import { useAuth } from "../../services"
import { PersonAccreditation } from "./PersonAccreditation"
import { PersonTrainingAssessment } from "./PersonTrainingAssessment"
import { PersonDetails } from "./PersonDetails"

const PersonViewer = ({ user, onClose, isInline }) => {
  const { action } = useParams()
  const [resendInvite] = useMutationResendInvite()
  const [loadAdminableUser] = useLazyQueryAdminableUser({ variables: { id: toId(user) } })
  const [tab, setTab] = useState(action || "details")
  const { hasFeature } = useAuth()

  const handleResendInvite = async () => {
    await resendInvite({ variables: { id: user.id } })
    await loadAdminableUser()
  }

  const handleOnClose = (event, isCancel) => {
    onClose(isCancel)
  }

  if (!user) {
    return null
  }

  const hasTraining = hasFeature("training")
  const hasAccreditation = hasFeature("accreditation")
  const hasTrainingAssessment = hasTraining && hasFeature("training_assessment")
  const hasTabs = hasTraining || hasAccreditation

  const display = (
    <>
      <Box px={isInline ? 2 : 0} py={isInline ? 2 : 0} data-cy="PersonViewer-display">
        <UserPanel user={user} layout="landscape" mb={2} />

        {hasTabs && (
          <Tabs
            variant="fullWidth"
            value={tab}
            onChange={(event, newTab) => setTab(newTab)}
            data-cy="PersonViewer-tabs"
          >
            <Tab label="Details" value="details" data-cy="Tab-details" />
            {hasTraining && <Tab label="Training courses" value="training" data-cy="Tab-training" />}
            {hasTrainingAssessment && (
              <Tab label="Training assessment" value="training-assessment" data-cy="Tab-training-assessment" />
            )}
            {hasAccreditation && <Tab label="Accreditations" value="accreditation" data-cy="Tab-accreditation" />}
          </Tabs>
        )}

        {hasTabs && <Divider />}

        <Box pt={2} data-cy="PersonViewer-page">
          {tab === "details" && <PersonDetails user={user} onResendInvite={handleResendInvite} />}
          {tab === "training" && <PersonTraining user={user} />}
          {tab === "training-assessment" && <PersonTrainingAssessment user={user} />}
          {tab === "accreditation" && <PersonAccreditation user={user} />}
        </Box>
      </Box>
    </>
  )

  if (isInline) {
    return (
      <>
        <InlineTitle>
          Personnel details
          <InlineTitleCloser onClose={handleOnClose} />
        </InlineTitle>
        {display}
        {tab === "details" && (
          <RowBox pr={2} pb={2} justifyContent="flex-end">
            <Button component={NavLink} variant="contained" color="primary" to={`/people/${user.id}/edit`}>
              Edit details
            </Button>
          </RowBox>
        )}
      </>
    )
  }
  return (
    <Dialog open={user !== null} onClose={handleOnClose} aria-labelledby="form-dialog-title" fullWidth maxWidth="md">
      <DialogTitle id="form-dialog-title">
        Personnel details
        <DialogTitleCloser onClose={handleOnClose} />
      </DialogTitle>
      <DialogContent>{display}</DialogContent>
      {tab === "details" && (
        <DialogActions>
          <Box pt={1} pr={2} pb={2}>
            <Button component={NavLink} variant="contained" color="primary" to={`/people/${user.id}/edit`}>
              Edit details
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  )
}

export { PersonViewer }
